import { Card, CardMedia, Box, IconButton, useTheme, CardContent, FormControl, InputLabel, Select, MenuItem, TextField, Button, Collapse, Menu, Typography, Tooltip, Snackbar } from "@mui/material";
import { useState } from "react";
import PaidComponent from "./pay";
import { Image } from "../../../../interfaces";
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ShareIcon from '@mui/icons-material/Share';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import PaidIcon from '@mui/icons-material/Paid';
import logger from 'src/logger';

// const priceOptions = [1, 2, 5, 10, 20, 50, 100];

export const ImageCard: React.FC<{
    image: Image;
    index: number;
    isMobile: boolean;
    edited: boolean;
    onMetadataChange: (index: number, field: 'price' | 'tags' | 'species' | 'weight', value: string | number) => void;
    onDeleteClick: (imageId: string) => void;
    onUpdateMetadata: (imageId: string) => void;
    onImageClick: (imagePath: string) => void;
    expanded: boolean;
    toggleExpand: () => void;
}> = ({
    image,
    index,
    isMobile,
    edited,
    onMetadataChange,
    onDeleteClick,
    onUpdateMetadata,
    onImageClick,
    expanded,
    toggleExpand,
}) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const isMenuOpen = Boolean(anchorEl);
    const isMoreMenuOpen = Boolean(moreMenuAnchorEl);

    const handleShareClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMoreMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setMoreMenuAnchorEl(event.currentTarget);
    };

    const handleMoreMenuClose = () => {
        setMoreMenuAnchorEl(null);
    };

    const shareUrl = `${window.location.origin}/image/${image.id}`;

    const handleShareToFacebook = () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        handleMenuClose();
    };

    const handleShareToWhatsApp = () => {
        window.open(`https://wa.me/?text=${encodeURIComponent(shareUrl)}`, '_blank');
        handleMenuClose();
    };

    const handleShareToEmail = () => {
        window.location.href = `mailto:?subject=Check out this image&body=${encodeURIComponent(shareUrl)}`;
        handleMenuClose();
    };

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                setOpenSnackbar(true);
                logger.info('URL copied to clipboard:', shareUrl);
            })
            .catch((err) => {
                logger.error('Failed to copy URL:', err);
                // Fallback for browsers that don't support clipboard API
                window.prompt('Copy to clipboard: Ctrl+C, Enter', shareUrl);
            });
        handleMenuClose();
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    return (
        <Card
            sx={{
                maxWidth: isMobile ? 345 : 500,
                backgroundColor: edited ? theme.palette.warning.light : theme.palette.background.paper,
            }}
        >
            <CardMedia
                sx={{ height: 0, paddingTop: '56.25%', cursor: 'pointer', position: 'relative' }}
                image={image.path}
                onClick={() => onImageClick(image.path)}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        m: 1,
                        display: 'flex',
                        gap: 1,
                    }}
                >
                    {/* Location Visibility Indicator */}
                    <Tooltip title={image.publicise ? "Location is public" : "Location is private"}>
                        <Box
                            sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: '50%',
                            }}
                        >
                            <IconButton color={image.publicise ? "success" : "default"} aria-label="location visibility">
                                {image.publicise ? <LocationOnIcon /> : <LocationOffIcon />}
                            </IconButton>
                        </Box>
                    </Tooltip>

                    {/* Cost Indicator */}
                    {image.publicise && image.price > 0 && (
                        <Tooltip title={`Location access costs £${image.price}`}>
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '32px',
                                    height: '32px',
                                }}
                            >
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: 'warning.main',
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    £{image.price}
                                </Typography>
                            </Box>
                        </Tooltip>
                    )}

                    {/* Zoom Icon */}
                    <Box
                        sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '50%',
                        }}
                    >
                        <IconButton color="primary" aria-label="view image" component="span">
                            <ZoomInIcon />
                        </IconButton>
                    </Box>
                </Box>
            </CardMedia>
            <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
                {/* Price Field */}
                {image.chargeForLocation && (
                    <TextField
                        sx={{ width: '100%' }}
                        size="small"
                        label="Price"
                        value={`£${image.price}`}
                        InputProps={{
                            readOnly: true,
                        }}
                        variant="outlined"
                    />
                )}

                {/* Description Field */}
                <TextField
                    sx={{ width: '100%', mt: 1 }}
                    size="small"
                    label="Description"
                    multiline
                    rows={2}
                    value={image.tags || ''}
                    onChange={(e) => onMetadataChange(index, 'tags', e.target.value)}
                />
                {image.area && (
                    <TextField
                        sx={{ width: '100%', mt: 1 }}
                        size="small"
                        label="Area"
                        value={`${image.area}`}
                        InputProps={{
                            readOnly: true,
                        }}
                        variant="outlined"
                    />
                )}

                {image.dateTaken && (
                    <TextField
                        sx={{ width: '100%', mt: 1 }}
                        size="small"
                        label="Date"
                        value={new Date(image.dateTaken).toLocaleString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                        InputProps={{
                            readOnly: true,
                        }}
                        variant="outlined"
                    />
                )}

                {/* Species Field */}
                <TextField
                    sx={{ width: '100%', mt: 1 }}
                    size="small"
                    label="Species"
                    value={image.species || ''}
                    onChange={(e) => onMetadataChange(index, 'species', e.target.value)}
                />

                {/* Weight Field */}
                <TextField
                    sx={{ width: '100%', mt: 1 }}
                    size="small"
                    label="Weight"
                    value={image.weight || ''}
                    onChange={(e) => onMetadataChange(index, 'weight', e.target.value)}
                />
            </CardContent>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, gap: 1 }}>
                <Button
                    color="primary"
                    size="small"
                    startIcon={<ShareIcon />}
                    onClick={handleShareClick}
                >
                    Share
                </Button>

                {edited && (
                    <Button
                        color="primary"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={() => onUpdateMetadata(image.id)}
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            }
                        }}
                    >
                        Save
                    </Button>
                )}

                <Button
                    color="primary"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => window.open(shareUrl, '_blank')}
                >
                    Open
                </Button>

                <Button
                    color="error"
                    size="small"
                    startIcon={<DeleteForeverIcon />}
                    onClick={() => onDeleteClick(image.id)}
                >
                    Delete
                </Button>

                {/* Share Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MenuItem onClick={handleShareToFacebook}>Share to Facebook</MenuItem>
                    <MenuItem onClick={handleShareToWhatsApp}>Share to WhatsApp</MenuItem>
                    <MenuItem onClick={handleShareToEmail}>Share via Email</MenuItem>
                    <MenuItem onClick={handleCopyUrl}>Copy URL</MenuItem>
                </Menu>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent>
                    <PaidComponent imageId={image.id} />
                </CardContent>
            </Collapse>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                message="URL copied to clipboard"
            />
        </Card>
    );
};
